/** 请求取消边界延伸至响应体消费结束；未消费响应不注册常驻监听器。 */
export function guardResponse(response: Response, signal: AbortSignal): Response {
  const check = () => { if (signal.aborted) throw new DOMException("Request cancelled", "AbortError"); };
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  const body = response.body ? new ReadableStream<Uint8Array>({
    async pull(controller) {
      let onAbort: (() => void) | undefined;
      try {
        check();
        reader ??= response.body!.getReader();
        const result = await Promise.race([
          reader.read(),
          new Promise<never>((_resolve, reject) => {
            onAbort = () => {
              void reader?.cancel().catch(() => undefined);
              reject(new DOMException("Request cancelled", "AbortError"));
            };
            signal.addEventListener("abort", onAbort, { once: true });
            if (signal.aborted) onAbort();
          }),
        ]);
        check();
        if (result.done) { reader.releaseLock(); controller.close(); }
        else controller.enqueue(result.value);
      } catch (error) {
        reader?.releaseLock();
        reader = undefined;
        controller.error(error);
      }
      finally { if (onAbort) signal.removeEventListener("abort", onAbort); }
    },
    async cancel(reason) {
      if (reader) { try { await reader.cancel(reason); } finally { reader.releaseLock(); reader = undefined; } }
      else await response.body?.cancel(reason);
    },
  }, { highWaterMark: 0 }) : null;
  const guarded = new Response(body, {
    status: response.status, statusText: response.statusText, headers: response.headers,
  });
  // JSON 解码也可能跨越一个微任务；每个公开消费方法返回前再次核对取消状态。
  for (const method of ["json", "text", "blob", "arrayBuffer", "formData"] as const) {
    const consume = guarded[method].bind(guarded);
    Object.defineProperty(guarded, method, { value: async () => {
      check();
      const value: unknown = await consume();
      check();
      return value;
    } });
  }
  return guarded;
}
